import React, { useState, useEffect } from 'react';
import {
  ChevronDown,
  ChevronUp,
  Plus,
  Search,
  Pencil,
  Trash2,
  Check,
  X,
  TrendingUp,
  TrendingDown,
  BarChart3,
  Layers,
  GitBranch,
} from 'lucide-react';
import { theme } from '../../styles/theme';
import metricsApi, { 
  Metric, 
  Cluster,
  SubDimension,
} from '../../api/metricsApi';
import { MetricFormWizard } from './MetricFormWizard';
import { ClusterManagement } from './ClusterManagement';
import { SubDimensionManagement } from './SubDimensionManagement';

interface MetricsManagementProps {
  darkMode?: boolean;
}

interface ClusterGroup {
  cluster: Cluster;
  metrics: Metric[];
  expanded: boolean;
}

export const MetricsManagement: React.FC<MetricsManagementProps> = ({ darkMode = false }) => {
  const [activeSubTab, setActiveSubTab] = useState<'metrics' | 'clusters' | 'subdimensions'>('metrics');
  const [metrics, setMetrics] = useState<Metric[]>([]);
  const [clusters, setClusters] = useState<Cluster[]>([]);
  const [subDimensions, setSubDimensions] = useState<SubDimension[]>([]);
  const [clusterGroups, setClusterGroups] = useState<ClusterGroup[]>([]);
  const [loading, setLoading] = useState(true);
  const [error, setError] = useState<string | null>(null);
  const [selectedMetric, setSelectedMetric] = useState<Metric | null>(null);
  const [formWizardOpen, setFormWizardOpen] = useState(false);
  const [searchTerm, setSearchTerm] = useState('');

  useEffect(() => {
    fetchData();
  }, []);

  const fetchData = async () => {
    try {
      setLoading(true);
      
      // Fetch data individually to identify which call fails
      let metricsData: Metric[] = [];
      let clustersData: Cluster[] = [];
      let subDimensionsData: SubDimension[] = [];
      
      try {
        metricsData = await metricsApi.getMetrics();
      } catch (err) {
        console.error('Failed to fetch metrics:', err);
        metricsData = [];
      }
      
      try {
        clustersData = await metricsApi.getClusters();
      } catch (err) {
        console.error('Failed to fetch clusters:', err);
        clustersData = [];
      }
      
      try {
        subDimensionsData = await metricsApi.getSubDimensions();
      } catch (err) {
        console.error('Failed to fetch subdimensions:', err);
        subDimensionsData = [];
      }

      setMetrics(metricsData || []);
      setClusters(clustersData || []);
      setSubDimensions(subDimensionsData || []);

      // Debug: Log the metrics data to see what we're getting
      console.log('Metrics data:', metricsData);
      console.log('First metric valueCount:', metricsData?.[0]?.valueCount);

      // Group metrics by cluster
      let groups: ClusterGroup[] = (clustersData || []).map(cluster => ({
        cluster,
        metrics: (metricsData || [])
          .filter(m => {
            if (m.cluster?.code === cluster.code) return true;
            if (m.metricId && m.metricId.startsWith(cluster.code + '.')) return true;
            return false;
          })
          .sort((a, b) => {
            // Extract numeric part from metric IDs like C1.10, C1.2, etc.
            const getNumericPart = (metricId: string) => {
              const match = metricId.match(/\.(\d+)/);
              return match ? parseInt(match[1], 10) : 0;
            };
            
            const numA = getNumericPart(a.metricId || '');
            const numB = getNumericPart(b.metricId || '');
            
            return numA - numB;
          }),
        expanded: false,
      }));

      // If we have no clusters but have metrics, create default groups from metric IDs
      if (groups.length === 0 && metricsData.length > 0) {
        const clusterCodes = new Set<string>();
        metricsData.forEach(m => {
          if (m.metricId) {
            const code = m.metricId.split('.')[0];
            clusterCodes.add(code);
          }
        });
        
        groups = Array.from(clusterCodes).map(code => ({
          cluster: { id: 0, code, name: `Cluster ${code}`, description: '' },
          metrics: metricsData
            .filter(m => m.metricId && m.metricId.startsWith(code + '.'))
            .sort((a, b) => {
              // Extract numeric part from metric IDs like C1.10, C1.2, etc.
              const getNumericPart = (metricId: string) => {
                const match = metricId.match(/\.(\d+)/);
                return match ? parseInt(match[1], 10) : 0;
              };
              
              const numA = getNumericPart(a.metricId || '');
              const numB = getNumericPart(b.metricId || '');
              
              return numA - numB;
            }),
          expanded: false,
        }));
      }
      
      setClusterGroups(groups);
      setError(null);
    } catch (err) {
      console.error('Error in fetchData:', err);
      setError('An unexpected error occurred. Please try again.');
    } finally {
      setLoading(false);
    }
  };

  const handleToggleCluster = (clusterCode: string) => {
    setClusterGroups(prev =>
      prev.map(group =>
        group.cluster.code === clusterCode
          ? { ...group, expanded: !group.expanded }
          : group
      )
    );
  };


  const handleOpenFormWizard = (metric?: Metric) => {
    setSelectedMetric(metric || null);
    setFormWizardOpen(true);
  };

  const handleCloseFormWizard = () => {
    setFormWizardOpen(false);
    setSelectedMetric(null);
  };

  const handleFormSuccess = () => {
    fetchData();
    handleCloseFormWizard();
  };

  const handleDeleteMetric = async (metricId: string) => {
    try {
      await metricsApi.deleteMetricData(metricId);
      await fetchData();
    } catch (err) {
      console.error('Error deleting metric:', err);
      setError('Failed to delete metric. Please try again.');
    }
  };


  const filteredGroups = clusterGroups.map(group => ({
    ...group,
    metrics: group.metrics.filter(metric =>
      searchTerm === '' ||
      metric.name.toLowerCase().includes(searchTerm.toLowerCase()) ||
      metric.searchName?.toLowerCase().includes(searchTerm.toLowerCase()) ||
      metric.metricId.toLowerCase().includes(searchTerm.toLowerCase())
    ),
  }));

  // Calculate stats from actual data
  const totalMetrics = metrics.length;
  const totalDataPoints = metrics.reduce((sum, m) => sum + (m.valueCount || 0), 0);
  const uniqueQualityLevels = 4; // Fixed at 4 based on metric collection service design

  const tabs = [
    { id: 'metrics', label: 'Metrics', icon: BarChart3 },
    { id: 'clusters', label: 'Clusters', icon: Layers },
    { id: 'subdimensions', label: 'Sub-dimensions', icon: GitBranch },
  ];

  if (loading && activeSubTab === 'metrics') {
    return (
      <div className="flex justify-center items-center min-h-[400px]">
        <div className="animate-spin rounded-full h-8 w-8 border-b-2" 
             style={{ borderColor: theme.colors.primary.main }}></div>
      </div>
    );
  }

  return (
    <div className="space-y-6">
      {/* Sub-tabs */}
      <div className="bg-white rounded-lg shadow-sm">
        <div className="border-b border-gray-200">
          <nav className="-mb-px flex">
            {tabs.map((tab) => {
              const Icon = tab.icon;
              const isActive = activeSubTab === tab.id;
              return (
                <button
                  key={tab.id}
                  onClick={() => setActiveSubTab(tab.id as typeof activeSubTab)}
                  className={`
                    flex-1 py-4 px-6 text-center border-b-2 font-medium text-sm
                    transition-colors duration-200 flex items-center justify-center gap-2
                    ${isActive 
                      ? 'border-[#FAAF90] text-[#E59873]' 
                      : 'border-transparent text-gray-500 hover:text-gray-700 hover:border-gray-300'
                    }
                  `}
                  style={{ fontFamily: '"Tenor Sans", sans-serif' }}
                >
                  <Icon className="w-5 h-5" />
                  {tab.label}
                </button>
              );
            })}
          </nav>
        </div>
      </div>

      {/* Tab Content */}
      {activeSubTab === 'metrics' && (
        <div className="space-y-6">
          {/* Header */}
          <div className="flex items-center justify-between mb-2">
            <div>
              <h2
                className="text-lg font-semibold"
                style={{
                  color: darkMode
                    ? theme.colors.text.darkPrimary
                    : theme.colors.text.primary,
                  fontFamily: theme.fonts.primary,
                }}
              >
                Metrics Management
              </h2>
              <p
                className="text-sm mt-1"
                style={{ 
                  color: theme.colors.text.secondary,
                  fontFamily: theme.fonts.primary,
                }}
              >
                Define and manage impact assessment metrics across clusters and sub-dimensions
              </p>
            </div>
            <button
              onClick={() => handleOpenFormWizard()}
              className="px-4 py-2 rounded-lg transition-colors flex items-center space-x-2 hover:opacity-90"
              style={{
                backgroundColor: theme.colors.primary.main,
                color: 'white',
                fontFamily: theme.fonts.primary,
              }}
            >
              <Plus className="w-4 h-4" />
              <span>New Metric</span>
            </button>
          </div>

      {/* Stats Cards */}
      <div className="grid grid-cols-4 gap-4">
        <div 
          className="p-4 rounded-lg text-center"
          style={{
            backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
            boxShadow: theme.widget.boxShadow,
          }}
        >
          <div className="text-2xl font-bold" style={{ color: theme.colors.primary.main }}>
            {totalMetrics}
          </div>
          <div className="text-sm mt-1" style={{ color: theme.colors.text.secondary }}>
            Total Metrics
          </div>
        </div>
        
        <div 
          className="p-4 rounded-lg text-center"
          style={{
            backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
            boxShadow: theme.widget.boxShadow,
          }}
        >
          <div className="text-2xl font-bold" style={{ color: theme.colors.status.success }}>
            {totalDataPoints}
          </div>
          <div className="text-sm mt-1" style={{ color: theme.colors.text.secondary }}>
            Data Points
          </div>
        </div>
        
        <div 
          className="p-4 rounded-lg text-center"
          style={{
            backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
            boxShadow: theme.widget.boxShadow,
          }}
        >
          <div className="text-2xl font-bold" style={{ color: theme.colors.secondary.main }}>
            {uniqueQualityLevels}
          </div>
          <div className="text-sm mt-1" style={{ color: theme.colors.text.secondary }}>
            Quality Levels
          </div>
        </div>
        
        <div 
          className="p-4 rounded-lg text-center"
          style={{
            backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
            boxShadow: theme.widget.boxShadow,
          }}
        >
          <div className="text-sm" style={{ color: theme.colors.text.secondary }}>
            Last Updated
          </div>
          <div className="text-lg font-medium mt-1" style={{ 
            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary 
          }}>
            {new Date().toLocaleDateString()}
          </div>
        </div>
      </div>

      {/* Search Bar */}
      <div className="relative w-full max-w-md">
        <Search className="absolute left-3 top-1/2 transform -translate-y-1/2 w-4 h-4"
                style={{ color: theme.colors.text.secondary }} />
        <input
          type="text"
          placeholder="Search metrics..."
          value={searchTerm}
          onChange={(e) => setSearchTerm(e.target.value)}
          className="pl-10 pr-4 py-2 rounded-lg border w-full"
          style={{
            backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
            borderColor: darkMode ? theme.colors.border.dark : theme.colors.border.light,
            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
            fontFamily: theme.fonts.primary,
          }}
        />
      </div>

      {error && (
        <div 
          className="p-4 rounded-lg flex items-center justify-between"
          style={{
            backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
            borderLeft: `4px solid ${theme.colors.status.error}`,
          }}
        >
          <span style={{ color: theme.colors.status.error }}>{error}</span>
          <button onClick={() => setError(null)} style={{ color: theme.colors.text.secondary }}>
            ×
          </button>
        </div>
      )}

      {/* Metrics Table */}
      <div 
        className="rounded-lg overflow-hidden"
        style={{
          backgroundColor: darkMode ? theme.colors.background.darkPaper : theme.colors.background.paper,
          border: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
          boxShadow: darkMode 
            ? '0 2px 8px rgba(0, 0, 0, 0.15)' 
            : '0 2px 10px rgba(228, 165, 128, 0.08)',
        }}
      >
        <table className="w-full" style={{ fontFamily: theme.fonts.primary }}>
          <thead>
            <tr style={{ 
              backgroundColor: darkMode 
                ? 'rgba(250, 175, 144, 0.04)' 
                : 'rgba(250, 175, 144, 0.06)',
              borderBottom: `2px solid ${theme.colors.primary.main}30` 
            }}>
              <th className="text-left px-4 py-3" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em'
              }}>Cluster</th>
              <th className="text-left px-4 py-3" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em'
              }}>Metric ID</th>
              <th className="text-left px-4 py-3" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em'
              }}>Metric Name</th>
              <th className="text-left px-4 py-3" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em'
              }}>Type</th>
              <th className="text-center px-4 py-3" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em'
              }}>Aggregatable</th>
              <th className="text-left px-4 py-3" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em'
              }}>Impact</th>
              <th className="text-left px-4 py-3" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em'
              }}>Aggregation</th>
              <th className="text-center px-4 py-3" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em'
              }}>Data Points</th>
              <th className="text-center px-4 py-3" style={{ 
                color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                fontSize: '14px',
                fontWeight: 600,
                letterSpacing: '0.025em'
              }}>Actions</th>
            </tr>
          </thead>
          <tbody>
            {filteredGroups.map((group) => (
              <React.Fragment key={group.cluster.code}>
                {/* Cluster Header Row */}
                <tr 
                  className="cursor-pointer transition-all duration-200 hover:shadow-sm"
                  onClick={() => handleToggleCluster(group.cluster.code)}
                  style={{
                    backgroundColor: darkMode 
                      ? 'rgba(255, 255, 255, 0.03)' 
                      : 'rgba(250, 175, 144, 0.05)',
                    borderTop: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                    borderBottom: group.expanded 
                      ? `2px solid ${theme.colors.primary.main}40`
                      : `1px solid transparent`,
                  }}
                  onMouseEnter={(e) => {
                    e.currentTarget.style.backgroundColor = darkMode 
                      ? 'rgba(255, 255, 255, 0.06)' 
                      : 'rgba(250, 175, 144, 0.10)';
                  }}
                  onMouseLeave={(e) => {
                    e.currentTarget.style.backgroundColor = darkMode 
                      ? 'rgba(255, 255, 255, 0.03)' 
                      : 'rgba(250, 175, 144, 0.05)';
                  }}
                >
                  <td colSpan={9} className="px-4 py-3">
                    <div className="flex items-center gap-3">
                      {group.expanded ? 
                        <ChevronUp className="w-4 h-4" style={{ color: theme.colors.text.secondary }} /> : 
                        <ChevronDown className="w-4 h-4" style={{ color: theme.colors.text.secondary }} />
                      }
                      <span 
                        className="px-2.5 py-1 rounded-md text-xs font-semibold"
                        style={{
                          backgroundColor: theme.colors.primary.main + '30',
                          color: theme.colors.primary.dark,
                          border: `1px solid ${theme.colors.primary.main}50`,
                        }}
                      >
                        {group.cluster.code}
                      </span>
                      <span className="text-base" style={{ 
                        color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                        fontFamily: theme.fonts.primary,
                        fontWeight: 500,
                      }}>
                        {group.cluster.name}
                      </span>
                      <span className="text-sm" style={{ 
                        color: theme.colors.text.secondary,
                        fontFamily: theme.fonts.primary,
                      }}>
                        ({group.metrics.length} metrics)
                      </span>
                    </div>
                  </td>
                </tr>

                {/* Metric Rows */}
                {group.expanded && (
                  group.metrics.length === 0 ? (
                    <tr>
                      <td colSpan={9} className="text-center py-6" style={{ 
                        color: theme.colors.text.secondary,
                        fontSize: '14px',
                        fontFamily: theme.fonts.primary,
                        fontStyle: 'italic',
                      }}>
                        No metrics in this cluster
                      </td>
                    </tr>
                  ) : (
                    group.metrics.map((metric) => (
                      <tr 
                        key={metric.metricId}
                        className="hover:bg-opacity-30 transition-colors"
                        style={{
                          borderBottom: `1px solid ${darkMode ? theme.colors.border.dark : theme.colors.border.light}`,
                        }}
                      >
                        <td className="px-4 py-3"></td>
                        <td className="px-4 py-3">
                          <span style={{ 
                            color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                            fontSize: '13px',
                            fontWeight: 500,
                            fontFamily: theme.fonts.primary,
                          }}>
                            {metric.metricId}
                          </span>
                        </td>
                        <td className="px-4 py-3" style={{ 
                          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                          fontFamily: theme.fonts.primary,
                          fontSize: '14px',
                          fontWeight: 500,
                        }}>
                          {metric.name}
                        </td>
                        <td className="px-4 py-3">
                          <span 
                            className="px-2.5 py-1 rounded-md"
                            style={{
                              backgroundColor: darkMode 
                                ? 'rgba(107, 155, 209, 0.15)' 
                                : 'rgba(107, 155, 209, 0.12)',
                              color: theme.colors.secondary.main,
                              fontSize: '12px',
                              fontWeight: 600,
                              fontFamily: theme.fonts.primary,
                              letterSpacing: '0.02em',
                            }}
                          >
                            {metric.metricType || 'Unknown'}
                          </span>
                        </td>
                        <td className="px-4 py-3 text-center">
                          {metric.isAggregatable ? (
                            <Check 
                              className="w-4 h-4 inline-block" 
                              style={{ 
                                color: theme.colors.status.success,
                                strokeWidth: 2.5,
                              }} 
                            />
                          ) : (
                            <X 
                              className="w-4 h-4 inline-block" 
                              style={{ 
                                color: theme.colors.text.secondary,
                                strokeWidth: 2,
                              }} 
                            />
                          )}
                        </td>
                        <td className="px-4 py-3 text-center">
                          {metric.impactDirection === 'positive' ? (
                            <TrendingUp 
                              className="w-4 h-4 inline-block" 
                              style={{ 
                                color: theme.colors.status.success,
                                strokeWidth: 2,
                              }} 
                            />
                          ) : metric.impactDirection === 'negative' ? (
                            <TrendingDown 
                              className="w-4 h-4 inline-block" 
                              style={{ 
                                color: theme.colors.status.error,
                                strokeWidth: 2,
                              }} 
                            />
                          ) : (
                            <span style={{ 
                              color: theme.colors.text.secondary,
                              fontSize: '13px',
                              fontFamily: theme.fonts.primary,
                            }}>
                              -
                            </span>
                          )}
                        </td>
                        <td className="px-4 py-3" style={{ 
                          color: darkMode ? theme.colors.text.darkSecondary : theme.colors.text.secondary,
                          fontSize: '13px',
                          fontFamily: theme.fonts.primary,
                        }}>
                          {metric.aggregationMethod || 'sum'}
                        </td>
                        <td className="px-4 py-3 text-center" style={{ 
                          color: darkMode ? theme.colors.text.darkPrimary : theme.colors.text.primary,
                          fontSize: '14px',
                          fontWeight: 600,
                          fontFamily: theme.fonts.primary,
                        }}>
                          {metric.valueCount || 0}
                        </td>
                        <td className="px-4 py-3">
                          <div className="flex justify-center items-center gap-2">
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleOpenFormWizard(metric);
                              }}
                              className="p-1.5 rounded-md hover:bg-opacity-10 transition-colors"
                              title="Edit metric"
                              style={{
                                color: theme.colors.text.secondary,
                              }}
                            >
                              <Pencil className="w-4 h-4" />
                            </button>
                            <button
                              onClick={(e) => {
                                e.stopPropagation();
                                handleDeleteMetric(metric.metricId);
                              }}
                              className="p-1.5 rounded-md hover:bg-opacity-10 transition-colors"
                              title="Delete metric data"
                              style={{
                                color: theme.colors.status.error,
                              }}
                            >
                              <Trash2 className="w-4 h-4" />
                            </button>
                          </div>
                        </td>
                      </tr>
                    ))
                  )
                )}
              </React.Fragment>
            ))}
          </tbody>
        </table>
      </div>

          {/* Form Wizard */}
          {formWizardOpen && (
            <MetricFormWizard
              open={formWizardOpen}
              onClose={handleCloseFormWizard}
              onSuccess={handleFormSuccess}
              metric={selectedMetric || undefined}
              clusters={clusters}
              subDimensions={subDimensions}
            />
          )}
        </div>
      )}
      
      {activeSubTab === 'clusters' && (
        <ClusterManagement />
      )}
      
      {activeSubTab === 'subdimensions' && (
        <SubDimensionManagement />
      )}
    </div>
  );
};